import { createFileRoute, Link } from "@tanstack/react-router";
import { useCallback, useEffect, useState } from "react";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { ArrowLeft, GraduationCap, Loader2, Users, Copy, Check } from "lucide-react";
import { toast } from "sonner";
import { SignInRequired } from "@/components/SignInRequired";

const TITLE = "Parent & Teacher Mode - Inquira";
const DESC = "Share a read-only progress view with a parent, or run a class with assignments, class goals and progress reports.";

export const Route = createFileRoute("/family")({
  component: FamilyPage,
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESC },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESC },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
});

type Rpc = (name: string, args?: object) => Promise<{ data: unknown; error: { message: string } | null }>;

interface Space {
  as_student: Array<{ id: string; role: string; name: string }>;
  as_guardian: Array<{ id: string; role: string; student_id: string; name: string }>;
  open_invites: Array<{ code: string; role: string; expires_at: string }>;
}
interface Assignment {
  id: string; title: string; instructions: string | null; topic: string | null;
  kind: string; due_on: string | null; completed?: number; done?: boolean;
}
interface TeachingClass {
  id: string; name: string; subject: string | null; join_code: string; students: number;
  goals: Array<{ id: string; title: string; metric: string; target: number; period: string }>;
  assignments: Assignment[];
  roster: Array<{ student_id: string; name: string; level: number; week_xp: number; done: number }>;
}
interface EnrolledClass {
  id: string; name: string; subject: string | null; teacher: string;
  goals: Array<{ title: string; metric: string; target: number; period: string }>;
  assignments: Assignment[];
}
interface Classroom { teaching: TeachingClass[]; enrolled: EnrolledClass[] }

/** RPCs can return an error envelope (e.g. an expired session); never let that crash the page. */
function normalizeSpace(data: unknown): Space {
  const d = (data ?? {}) as Partial<Space>;
  return {
    as_student: Array.isArray(d.as_student) ? d.as_student : [],
    as_guardian: Array.isArray(d.as_guardian) ? d.as_guardian : [],
    open_invites: Array.isArray(d.open_invites) ? d.open_invites : [],
  };
}
function normalizeRoom(data: unknown): Classroom {
  const d = (data ?? {}) as Partial<Classroom>;
  return {
    teaching: Array.isArray(d.teaching) ? d.teaching : [],
    enrolled: Array.isArray(d.enrolled) ? d.enrolled : [],
  };
}
interface Report {
  student: { name: string; streak: number };
  progress: { xp: number; level: number; study_minutes: number; total_problems: number; total_quizzes: number } | null;
  week: Array<{ date: string; xp: number; events: number; minutes: number }>;
  goals_completed: number;
  goals_active: Array<{ title: string; progress: number; target: number }>;
  achievements: { quests_completed: number; chests_opened: number };
  subjects: Array<{ subject: string; events: number; accuracy: number | null }>;
}

function FamilyPage() {
  const { user, isLoading } = useAuth();
  // Call through the client so `this` stays bound (the client is a lazy Proxy).
  const rpc = useCallback<Rpc>((name, args) => (supabase.rpc as unknown as Rpc).call(supabase, name, args), []);
  const [space, setSpace] = useState<Space | null>(null);
  const [room, setRoom] = useState<Classroom | null>(null);
  const [report, setReport] = useState<Report | null>(null);
  const [loading, setLoading] = useState(true);
  const [joinCode, setJoinCode] = useState("");
  const [className, setClassName] = useState("");
  const [classSubject, setClassSubject] = useState("");

  const load = useCallback(async () => {
    const [s, c] = await Promise.all([rpc("my_guardian_space"), rpc("my_classroom")]);
    setSpace(normalizeSpace(s.data));
    setRoom(normalizeRoom(c.data));
    setLoading(false);
    if (s.error || c.error) toast.error("Could not load your parent & teacher space. Please try again.");
  }, [rpc]);

  useEffect(() => { if (user) load(); else setLoading(false); }, [user, load]);

  const invite = async (role: "parent" | "teacher") => {
    const { data } = await rpc("create_link_invite", { _role: role });
    const res = data as { ok?: boolean; code?: string };
    if (res?.ok) { toast.success(`Invite code ${res.code} created — valid for 7 days.`); load(); }
    else toast.error("Could not create an invite code.");
  };

  const redeem = async () => {
    const { data } = await rpc("redeem_link_invite", { _code: joinCode.trim() });
    const res = data as { ok?: boolean; error?: string };
    if (res?.ok) { toast.success("Linked. You can now view their progress."); setJoinCode(""); load(); }
    else toast.error(res?.error === "no_such_code" ? "That code doesn't exist." : res?.error === "expired" ? "That code expired." : "Could not use that code.");
  };

  const joinClass = async () => {
    const { data } = await rpc("join_class", { _code: joinCode.trim() });
    const res = data as { ok?: boolean; class?: string; error?: string };
    if (res?.ok) { toast.success(`Joined ${res.class}.`); setJoinCode(""); load(); }
    else toast.error(res?.error === "no_such_class" ? "No class uses that code." : "Could not join that class.");
  };

  const createClass = async () => {
    if (!className.trim()) return;
    const { data } = await rpc("teacher_create_class", { _name: className, _subject: classSubject });
    const res = data as { ok?: boolean; join_code?: string };
    if (res?.ok) { toast.success(`Class created. Join code: ${res.join_code}`); setClassName(""); setClassSubject(""); load(); }
    else toast.error("Could not create that class.");
  };

  const viewStudent = async (studentId: string) => {
    const { data } = await rpc("guardian_student_report", { _student_id: studentId });
    const res = data as Report & { error?: string };
    if (res?.error) { toast.error("You are not linked to that student."); return; }
    setReport(res);
  };

  if (isLoading || loading) {
    return <div className="min-h-screen flex items-center justify-center"><Loader2 className="size-6 animate-spin text-ink-muted" /></div>;
  }
  if (!user) return <SignInRequired title="Sign in to use parent & teacher mode" description="Share progress with a parent, or run a class with invite codes." />;

  return (
    <div className="min-h-screen max-w-3xl mx-auto px-4 py-6 space-y-6">
      <Link to="/" className="flex items-center gap-2 text-sm text-ink-muted hover:text-ink">
        <ArrowLeft className="size-4" /> Home
      </Link>

      <header>
        <h1 className="text-3xl font-display font-black text-ink">Parents &amp; teachers</h1>
        <p className="text-sm text-ink-muted mt-1">
          Guardians see time studied, progress, goals and achievements only — never your problems, chats or solutions.
        </p>
      </header>

      <section className="rounded-2xl border border-rule bg-card p-4 space-y-3">
        <h2 className="font-bold text-ink flex items-center gap-2"><Users className="size-4 text-neon-cyan" /> Share my progress</h2>
        <div className="flex flex-wrap gap-2">
          <button onClick={() => invite("parent")} className="rounded-xl border border-rule px-3 py-2 text-sm font-semibold text-ink hover:bg-rule/30">Invite a parent</button>
          <button onClick={() => invite("teacher")} className="rounded-xl border border-rule px-3 py-2 text-sm font-semibold text-ink hover:bg-rule/30">Invite a teacher</button>
        </div>
        {!!space?.open_invites.length && (
          <ul className="space-y-1">
            {space.open_invites.map((i) => (
              <li key={i.code} className="text-xs text-ink-muted flex items-center gap-2">
                <button onClick={() => { navigator.clipboard.writeText(i.code); toast.success("Code copied."); }} className="flex items-center gap-1.5 hover:text-ink">
                  <Copy className="size-3.5" /> <span className="font-mono font-bold text-ink">{i.code}</span>
                </button>
                for a {i.role} · expires {new Date(i.expires_at).toLocaleDateString()}
              </li>
            ))}
          </ul>
        )}
        {!!space?.as_student.length && (
          <p className="text-xs text-ink-muted">
            Currently shared with: {space.as_student.map((l) => `${l.name} (${l.role})`).join(", ")}
          </p>
        )}
      </section>

      <section className="rounded-2xl border border-rule bg-card p-4 space-y-3">
        <h2 className="font-bold text-ink">Have a code?</h2>
        <div className="flex items-center gap-2">
          <input
            value={joinCode}
            onChange={(e) => setJoinCode(e.target.value.toUpperCase())}
            placeholder="Invite or class code"
            aria-label="Invite or class code"
            className="flex-1 rounded-xl border border-rule bg-transparent px-3 py-2 text-sm text-ink"
          />
          <button onClick={redeem} className="rounded-xl bg-primary text-primary-foreground px-3 py-2 text-sm font-bold">Link student</button>
          <button onClick={joinClass} className="rounded-xl border border-rule px-3 py-2 text-sm font-bold text-ink">Join class</button>
        </div>
      </section>

      {!!space?.as_guardian.length && (
        <section className="rounded-2xl border border-rule bg-card p-4 space-y-2">
          <h2 className="font-bold text-ink">Students I follow</h2>
          {space.as_guardian.map((l) => (
            <button key={l.id} onClick={() => viewStudent(l.student_id)} className="w-full text-left rounded-xl border border-rule px-3 py-2 text-sm text-ink hover:bg-rule/30">
              {l.name} <span className="text-ink-muted text-xs">· {l.role} view</span>
            </button>
          ))}
        </section>
      )}

      {report && (
        <section className="rounded-2xl border border-rule bg-card p-4 space-y-3">
          <h2 className="font-bold text-ink">{report.student.name} — weekly report</h2>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-center">
            <Stat label="Level" value={report.progress?.level ?? 1} />
            <Stat label="Minutes" value={report.progress?.study_minutes ?? 0} />
            <Stat label="Streak" value={report.student.streak} />
            <Stat label="Goals done" value={report.goals_completed} />
          </div>
          <div className="flex items-end gap-1 h-24">
            {report.week.map((d) => {
              const max = Math.max(1, ...report.week.map((x) => Number(x.xp)));
              return (
                <div key={d.date} className="flex-1 flex flex-col items-center gap-1">
                  <div className="w-full rounded-t bg-primary/70" style={{ height: `${(Number(d.xp) / max) * 100}%` }} />
                  <span className="text-[10px] text-ink-muted">{new Date(d.date).toLocaleDateString(undefined, { weekday: "narrow" })}</span>
                </div>
              );
            })}
          </div>
          {!!report.goals_active.length && (
            <ul className="text-xs text-ink-muted space-y-1">
              {report.goals_active.map((g) => (
                <li key={g.title}>{g.title} — {Math.round(Number(g.progress))}/{Math.round(Number(g.target))}</li>
              ))}
            </ul>
          )}
          <p className="text-xs text-ink-muted">
            Achievements: {report.achievements.quests_completed} quests, {report.achievements.chests_opened} chests.
          </p>
          {!!report.subjects.length && (
            <p className="text-xs text-ink-muted">
              Subjects: {report.subjects.map((s) => `${s.subject}${s.accuracy != null ? ` ${s.accuracy}%` : ""}`).join(" · ")}
            </p>
          )}
        </section>
      )}

      <section className="rounded-2xl border border-rule bg-card p-4 space-y-3">
        <h2 className="font-bold text-ink flex items-center gap-2"><GraduationCap className="size-4 text-neon-pink" /> Teacher mode</h2>
        <div className="flex flex-wrap items-center gap-2">
          <input value={className} onChange={(e) => setClassName(e.target.value)} placeholder="Class name" aria-label="Class name"
            className="flex-1 min-w-40 rounded-xl border border-rule bg-transparent px-3 py-2 text-sm text-ink" />
          <input value={classSubject} onChange={(e) => setClassSubject(e.target.value)} placeholder="Subject (optional)" aria-label="Class subject"
            className="flex-1 min-w-40 rounded-xl border border-rule bg-transparent px-3 py-2 text-sm text-ink" />
          <button onClick={createClass} className="rounded-xl bg-primary text-primary-foreground px-4 py-2 text-sm font-bold">Create class</button>
        </div>
        {room?.teaching.map((c) => <TeachingCard key={c.id} c={c} onChange={load} rpc={rpc} />)}
      </section>

      {!!room?.enrolled.length && (
        <section className="rounded-2xl border border-rule bg-card p-4 space-y-3">
          <h2 className="font-bold text-ink">My classes</h2>
          {room.enrolled.map((c) => (
            <div key={c.id} className="rounded-xl border border-rule p-3 space-y-2">
              <p className="font-semibold text-ink">{c.name} <span className="text-xs text-ink-muted">· {c.teacher}</span></p>
              {c.goals.map((g) => (
                <p key={g.title} className="text-xs text-ink-muted">Class goal: {g.title} ({Math.round(Number(g.target))} {g.metric} / {g.period})</p>
              ))}
              {c.assignments.map((a) => (
                <div key={a.id} className="flex items-center justify-between gap-2 text-sm">
                  <span className="text-ink">
                    {a.title}
                    {a.due_on && <span className="text-xs text-ink-muted"> · due {new Date(a.due_on).toLocaleDateString()}</span>}
                  </span>
                  {a.done ? (
                    <span className="text-xs text-ink-muted flex items-center gap-1"><Check className="size-3.5" /> Done</span>
                  ) : (
                    <button
                      onClick={async () => { await rpc("complete_assignment", { _assignment_id: a.id, _note: "" }); toast.success("Marked complete."); load(); }}
                      className="text-xs font-bold rounded-full px-3 py-1 border border-rule text-ink hover:bg-rule/30"
                    >
                      Mark done
                    </button>
                  )}
                </div>
              ))}
              {!c.assignments.length && <p className="text-xs text-ink-muted">No assignments yet.</p>}
            </div>
          ))}
        </section>
      )}
    </div>
  );
}

function Stat({ label, value }: { label: string; value: number }) {
  return (
    <div className="rounded-xl border border-rule p-2">
      <p className="text-lg font-black text-ink">{value}</p>
      <p className="text-[10px] uppercase tracking-wide text-ink-muted">{label}</p>
    </div>
  );
}

function TeachingCard({ c, onChange, rpc }: { c: TeachingClass; onChange: () => void; rpc: Rpc }) {
  const [title, setTitle] = useState("");
  const [due, setDue] = useState("");
  const [goalTitle, setGoalTitle] = useState("");

  const addAssignment = async () => {
    if (!title.trim()) return;
    await rpc("teacher_create_assignment", {
      _class_id: c.id, _title: title, _instructions: "", _topic: "", _kind: "practice", _due_on: due || null,
    });
    setTitle(""); setDue(""); toast.success("Assignment created."); onChange();
  };

  const addGoal = async () => {
    if (!goalTitle.trim()) return;
    await rpc("teacher_create_class_goal", { _class_id: c.id, _title: goalTitle, _metric: "xp", _target: 500, _period: "weekly" });
    setGoalTitle(""); toast.success("Class goal created."); onChange();
  };

  return (
    <div className="rounded-xl border border-rule p-3 space-y-3">
      <div className="flex items-center justify-between">
        <p className="font-semibold text-ink">{c.name} <span className="text-xs text-ink-muted">· {c.students} students</span></p>
        <button onClick={() => { navigator.clipboard.writeText(c.join_code); toast.success("Join code copied."); }} className="text-xs font-mono font-bold text-ink flex items-center gap-1">
          <Copy className="size-3.5" /> {c.join_code}
        </button>
      </div>

      <div className="flex flex-wrap gap-2">
        <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="New assignment" aria-label="Assignment title"
          className="flex-1 min-w-40 rounded-lg border border-rule bg-transparent px-3 py-1.5 text-sm text-ink" />
        <input type="date" value={due} onChange={(e) => setDue(e.target.value)} aria-label="Due date"
          className="rounded-lg border border-rule bg-transparent px-3 py-1.5 text-sm text-ink" />
        <button onClick={addAssignment} className="rounded-lg border border-rule px-3 py-1.5 text-sm font-bold text-ink">Add</button>
      </div>
      <div className="flex flex-wrap gap-2">
        <input value={goalTitle} onChange={(e) => setGoalTitle(e.target.value)} placeholder="Weekly class goal (500 XP)" aria-label="Class goal"
          className="flex-1 min-w-40 rounded-lg border border-rule bg-transparent px-3 py-1.5 text-sm text-ink" />
        <button onClick={addGoal} className="rounded-lg border border-rule px-3 py-1.5 text-sm font-bold text-ink">Set goal</button>
      </div>

      {c.assignments.map((a) => (
        <p key={a.id} className="text-xs text-ink-muted">
          {a.title} — {a.completed ?? 0}/{c.students} completed{a.due_on ? ` · due ${new Date(a.due_on).toLocaleDateString()}` : ""}
        </p>
      ))}

      {!!c.roster.length && (
        <div className="pt-1">
          <p className="text-xs font-bold text-ink mb-1">Progress report</p>
          {c.roster.map((r) => (
            <p key={r.student_id} className="text-xs text-ink-muted">
              {r.name} — level {r.level}, {r.week_xp} XP this week, {r.done} assignments done
            </p>
          ))}
        </div>
      )}
    </div>
  );
}